import type { TranscriptSegment } from 'src/meetings/detail/tabs/transcript/types';
import type { CalendarEvent } from 'src/meetings/models';

// The backend's merge pipeline renames every unresolved diarisation label to a
// friendly "Unknown speaker N" (see UnknownSpeakerPresentationHeuristic), so
// that is the shape the UI actually receives. Raw "SPEAKER_n" is matched too as
// a safety net in case a transcript slips through without the presentation step.
const UNIDENTIFIED_SPEAKER_PATTERN = /^(SPEAKER_\d+|Unknown speaker \d+)$/i;

type UnidentifiedSpeaker = {
  label: string;
  // The label to send when correcting this speaker: the captured diarisation
  // label where present, so the backend matches on the original diarised speaker
  // rather than the display name. Falls back to `label` for older transcripts.
  correctionLabel: string;
  statementCount: number;
};

const isUnidentifiedSpeaker = (speaker: string): boolean =>
  UNIDENTIFIED_SPEAKER_PATTERN.test(speaker.trim());

// A speaker is either a placeholder nobody has named yet, or resolved to a named
// participant.
export type SpeakerStatus = 'unidentified' | 'confirmed';

export type MeetingSpeaker = {
  // The raw `segment.speaker` value, used to key segments and corrections.
  label: string;
  // The name shown in the UI: a friendly "Speaker N" while unidentified, or the
  // resolved participant name once identified.
  displayName: string;
  // Every diarisation label grouped under this display label. Usually one, but an
  // adviser can identify several unknown speakers as the same person, so all of
  // their labels must be corrected together rather than only the first seen.
  correctionLabels: string[];
  statementCount: number;
  status: SpeakerStatus;
  // Every voice is too short to be anyone on its own, so the speaker is not one still to identify.
  isFragment: boolean;
};

/**
 * Every distinct speaker in the transcript, in order of first appearance, with a
 * derived status: still-unidentified placeholders versus speakers already
 * resolved to a named participant. Backing data for the speaker review screen,
 * which lists everyone rather than only the ones left to identify.
 */
export const getSpeakers = (
  segments: TranscriptSegment[] | undefined,
  speakerFragments?: string[],
): MeetingSpeaker[] => {
  if (!segments) return [];

  const byLabel = new Map<string, { statementCount: number; correctionLabels: string[] }>();
  for (const segment of segments) {
    const correctionLabel = segment.diarisationLabel ?? segment.speaker;
    const existing = byLabel.get(segment.speaker);
    if (existing) {
      existing.statementCount += 1;
      if (!existing.correctionLabels.includes(correctionLabel)) {
        existing.correctionLabels.push(correctionLabel);
      }
    } else {
      byLabel.set(segment.speaker, { statementCount: 1, correctionLabels: [correctionLabel] });
    }
  }

  return [...byLabel.entries()].map(([label, { statementCount, correctionLabels }]) => {
    const status: SpeakerStatus = isUnidentifiedSpeaker(label) ? 'unidentified' : 'confirmed';
    return {
      label,
      displayName: status === 'unidentified' ? formatSpeakerLabel(label) : label,
      correctionLabels,
      statementCount,
      status,
      isFragment:
        speakerFragments !== undefined &&
        correctionLabels.every(correctionLabel => speakerFragments.includes(correctionLabel)),
    };
  });
};

/**
 * A diarised transcript labels each segment with a placeholder speaker label
 * ("Unknown speaker 1", or a raw "SPEAKER_0") until someone is identified. This
 * narrows {@link getSpeakers} to the still-unidentified labels so the grouping
 * and counting logic lives in one place. Fragments are left out, as the meetings
 * list leaves them out.
 */
export const getUnidentifiedSpeakers = (
  segments: TranscriptSegment[] | undefined,
  speakerFragments?: string[],
): UnidentifiedSpeaker[] =>
  getSpeakers(segments, speakerFragments)
    .filter(speaker => speaker.status === 'unidentified' && !speaker.isFragment)
    .map(({ label, correctionLabels, statementCount }) => ({
      label,
      correctionLabel: correctionLabels[0] ?? label,
      statementCount,
    }));

type SpeakerReviewPrompt = {
  count: number;
  // Completes "N speakers ...", e.g. "unidentified" or "awaiting confirmation".
  state: string;
  // The link text that follows that sentence.
  action: string;
  // Closes the sentence after the link, saying what reviewing buys.
  reason: string;
  // The speaker the link should open.
  speakerLabel: string;
};

/**
 * The nudge a meeting list shows about a recording's speakers, or none where nobody is
 * left to identify.
 *
 * A count without a label prompts for nothing: the nudge exists to open that speaker, and
 * the backend derives both from one aggregated row, so a count means a label.
 */
export const speakerReviewPrompt = (
  event: Pick<CalendarEvent, 'unidentifiedSpeakerCount' | 'firstUnidentifiedSpeakerLabel'>,
): SpeakerReviewPrompt | undefined => {
  if (event.unidentifiedSpeakerCount > 0 && event.firstUnidentifiedSpeakerLabel) {
    return {
      count: event.unidentifiedSpeakerCount,
      state: 'unidentified',
      action: 'Identify speakers',
      reason: 'for a more accurate note.',
      speakerLabel: event.firstUnidentifiedSpeakerLabel,
    };
  }
  return undefined;
};

/**
 * Turns a placeholder diarisation label into a friendlier display name, e.g.
 * "SPEAKER_0" becomes "Speaker 1" and "Unknown speaker 2" becomes "Speaker 2".
 * Anything else is shown as-is.
 */
export const formatSpeakerLabel = (label: string): string => {
  const rawSpeakerMatch = label.match(/^SPEAKER_(\d+)$/);
  if (rawSpeakerMatch) return `Speaker ${Number(rawSpeakerMatch[1]) + 1}`;

  const unknownSpeakerMatch = label.match(/^Unknown speaker (\d+)$/i);
  if (unknownSpeakerMatch) return `Speaker ${Number(unknownSpeakerMatch[1])}`;

  return label;
};
